/**
 * THE CONVERSATION KEPT FOR THIS TAB (the owner, 2026-10-17 ~17:10: "Keep it
 * for this tab").
 *
 * Where history is off — an administrator's switch on the engine (decision
 * 27), or a deployment that keeps none — the store keeps no conversation. The
 * Assistant is a page of the SOC route, and Processes, Devices and VPN are
 * documents of their own, so leaving the page for them — or reloading — ended
 * the conversation in progress. It is kept now, for this tab only:
 *  - in this tab's sessionStorage, never in localStorage and never on the
 *    server; a closed tab takes it with it;
 *  - bound as the carry is (carry.ts): to this tab's own customer selection,
 *    the account whoami named when the conversation began, and the engine's
 *    per-session CSRF cookie — a page under another customer, account or
 *    session never shows it and drops it;
 *  - cleared by sign-out (AssistantChatProvider), by a customer switch, and by
 *    New conversation (the page writes nothing for an empty conversation).
 * Where history IS kept (the control plane) nothing is written here.
 *
 * THE QUESTION BEING WRITTEN is kept the same way, in a record of its own
 * (KEPT_DRAFT_KEY), on the same rules (close10 review b2): a question typed
 * and not sent comes back after Processes and a reload in this tab, and is
 * cleared by sign-out, a customer switch, another identity or session, and by
 * sending it or clearing it. It is a record of its own because a question can
 * be typed before there is any conversation to keep. The browser's leave
 * guard still asks before it is put at risk (AssistantPage: useUnsentWork).
 *
 * Only a record well formed throughout is read; anything else is dropped whole.
 */
import type { ChatMessage } from "./chatApi";

export const KEPT_KEY = "assistant.unsaved";
export const KEPT_DRAFT_KEY = "assistant.draft";

export interface KeptConversation {
  v: 1;
  /** This tab's own customer selection when it was written (selectedTenantNow), or null. */
  tenant: string | null;
  /** The account whoami named when the conversation began. */
  user: string;
  /** The engine's per-session CSRF cookie; "" where the plane sets none. */
  session: string;
  messages: ChatMessage[];
  /**
   * What the conversation is about, when a drill handed it over (handover.ts
   * subjectKey): a later "Open in Assistant" about the same subject, from a
   * drill on another document, continues it rather than ending it.
   */
  about?: string;
  /** The drill conversations it continues (their ids), newest last. */
  from?: string[];
  /**
   * Kept because the history list never answered within LIST_WAIT_MS (r4),
   * not because history was off: what the line offering it back says. Only
   * ever true; anything else drops the record whole.
   */
  late?: true;
}

const isString = (v: unknown): v is string => typeof v === "string";

/** The most drill conversation ids a kept record names. */
export const MAX_FROM = 16;

/** One message read strictly: every field of the type it must be, or nothing. */
export function parseChatMessage(value: unknown): ChatMessage | null {
  return parseMessage(value);
}

function parseMessage(value: unknown): ChatMessage | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const r = value as Record<string, unknown>;
  if (!isString(r.id) || !isString(r.content) || (r.role !== "user" && r.role !== "assistant")) return null;
  return {
    id: r.id,
    chat_id: isString(r.chat_id) ? r.chat_id : "",
    role: r.role,
    content: r.content,
    grounded: r.grounded === true,
    created_at: isString(r.created_at) ? r.created_at : "",
    ...(isString(r.model) ? { model: r.model } : {}),
    ...(isString(r.steps) ? { steps: r.steps } : {}),
    ...(r.derived === true ? { derived: true } : {}),
    ...(r.no_claim === true ? { no_claim: true } : {})
  };
}

/** A record read strictly, or null. */
export function parseKept(raw: string): KeptConversation | null {
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const o = value as Record<string, unknown>;
  if (o.v !== 1) return null;
  if (!(o.tenant === null || isString(o.tenant))) return null;
  if (!isString(o.user) || !o.user || !isString(o.session)) return null;
  if (!Array.isArray(o.messages) || o.messages.length === 0) return null;
  const messages: ChatMessage[] = [];
  for (const m of o.messages) {
    const parsed = parseMessage(m);
    if (!parsed) return null;
    messages.push(parsed);
  }
  // What it is about and which drill conversations it continues: optional, and
  // read as strictly as the rest — a wrong type drops the record whole.
  if (!(o.about === undefined || (isString(o.about) && o.about.length <= 600))) return null;
  if (
    !(
      o.from === undefined ||
      (Array.isArray(o.from) && o.from.length <= MAX_FROM && o.from.every((id) => isString(id) && /^[A-Za-z0-9._:-]{1,64}$/.test(id)))
    )
  ) {
    return null;
  }
  if (!(o.late === undefined || o.late === true)) return null;
  return {
    v: 1,
    tenant: (o.tenant as string | null) ?? null,
    user: o.user,
    session: o.session,
    messages,
    ...(isString(o.about) && o.about ? { about: o.about } : {}),
    ...(Array.isArray(o.from) && o.from.length ? { from: [...(o.from as string[])] } : {}),
    ...(o.late === true ? { late: true as const } : {})
  };
}

/** The record this tab keeps, or null; a malformed one is removed. Storage that cannot be used keeps nothing. */
export function readKept(): KeptConversation | null {
  let raw: string | null;
  try {
    raw = window.sessionStorage.getItem(KEPT_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  const kept = parseKept(raw);
  if (!kept) clearKept();
  return kept;
}

export function writeKept(kept: KeptConversation): void {
  try {
    window.sessionStorage.setItem(KEPT_KEY, JSON.stringify(kept));
  } catch {
    // Blocked or full: the conversation lasts as long as the page, as before.
  }
}

export function clearKept(): void {
  try {
    window.sessionStorage.removeItem(KEPT_KEY);
  } catch {
    // Nothing could have been kept either.
  }
}

/** The question typed and not sent, bound as the conversation is. */
export interface KeptDraft {
  v: 1;
  /** This tab's own customer selection when it was written (selectedTenantNow), or null. */
  tenant: string | null;
  /** The account whoami named when the question began. */
  user: string;
  /** The engine's per-session CSRF cookie; "" where the plane sets none. */
  session: string;
  text: string;
}

/** A draft record read strictly, or null: blank text is no question. */
export function parseKeptDraft(raw: string): KeptDraft | null {
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const o = value as Record<string, unknown>;
  if (o.v !== 1) return null;
  if (!(o.tenant === null || isString(o.tenant))) return null;
  if (!isString(o.user) || !o.user || !isString(o.session)) return null;
  if (!isString(o.text) || !o.text.trim()) return null;
  return { v: 1, tenant: (o.tenant as string | null) ?? null, user: o.user, session: o.session, text: o.text };
}

/** The question this tab keeps, or null; a malformed record is removed. Storage that cannot be used keeps nothing. */
export function readKeptDraft(): KeptDraft | null {
  let raw: string | null;
  try {
    raw = window.sessionStorage.getItem(KEPT_DRAFT_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  const kept = parseKeptDraft(raw);
  if (!kept) clearKeptDraft();
  return kept;
}

export function writeKeptDraft(kept: KeptDraft): void {
  try {
    window.sessionStorage.setItem(KEPT_DRAFT_KEY, JSON.stringify(kept));
  } catch {
    // Blocked or full: the leave guard is the question's only keeper, as on the control plane.
  }
}

export function clearKeptDraft(): void {
  try {
    window.sessionStorage.removeItem(KEPT_DRAFT_KEY);
  } catch {
    // Nothing could have been kept either.
  }
}

/**
 * The key the composer's question is registered and marked under as unsent
 * work (common/form/unsentWork.ts): `data-unsent-key` on the composer form.
 */
export const ASSISTANT_QUESTION_KEY = "assistant-question";

/**
 * WHAT CLOSING THE ASSISTANT PAGE DOES TO A QUESTION NOT SENT, for the route's
 * Escape prompt (SocRoute.tsx), said as it is at the moment of asking. Closing
 * never loses it: the page keeps its body, so the question is in the composer
 * when the page opens again. Where this tab keeps it as well (history off, the
 * engine: the record names this very text) it survives a reload too; anywhere
 * else (the control plane, which keeps no unsent question; or a record not
 * written) it lasts while this tab is open, and the leave guard asks before a
 * reload or a leave.
 */
export function questionOnClose(typed: string): string {
  const kept = readKeptDraft();
  return kept !== null && kept.text === typed
    ? "Close this page? Your question to the assistant is not sent. It is kept in this tab: it will be in the composer when you open the Assistant again, after a reload too."
    : "Close this page? Your question to the assistant is not sent. It stays in the composer while this tab is open: opening the Assistant again shows it, and a reload or leaving the console asks first.";
}
